import cn from 'classnames';
import React, { useEffect } from 'react';
import { HeaderWrapper, HeaderRow, BodyWrapper, dataTableStyles } from '@dealroadshow/uikit/core/components/Table/DataTable';
import Spinner from '@dealroadshow/uikit/core/components/Loader/Spinner';
import Paginator from '@dealroadshow/uikit/core/components/Paginator';

import { useTraceTableContext } from '@/finsight/application/Trace/TraceTableContext';
import { useFiltersContext } from '@/finsight/application/FiltersContext';

import { getColumns } from './getTradesColumns';

import { DEFAULT_EMPTY_MESSAGE } from '@/Framework/UI/Molecules/Tables/constants';
import TradesRow from './TradesRow';
import { TableFooterLabel } from '@/finsight/ui/components/trace/common/TableFooterLabel';
import { TRACE_MODULE } from '@/condor/ui/components/FinsightManager/constants';
import { useTrackAnalytics } from '@/finsight/application/useTrackAnalytics';

import spacesStyles from '@dealroadshow/uikit/core/styles/helpers/spaces.scss';
import styles from '@/finsight/ui/components/bonds/common/BondTables/bondsTables.scss';
import traceStyles from '../trace.scss';

interface IProps {
  isItemsPerPageVisible: boolean,
  emptyMessage?: React.ReactNode,
}

const TraceTable = ({ isItemsPerPageVisible = true, emptyMessage = DEFAULT_EMPTY_MESSAGE }: IProps) => {
  const { traceData: { collection: data, totalCount }, isTraceFetching } = useTraceTableContext();
  const { trackAnalytics } = useTrackAnalytics();
  const {
    params: {
      sortBy,
      sortOrder,
      page,
      perPage,
    },
    handlePageChange,
    handleSortChange,
    handlePerPageChange,
  } = useFiltersContext();

  const paginate = (page: { selected: number }) => {
    handlePageChange(page.selected);
  };

  const itemsPerPage = (perPage: { value: number, label: string }) => {
    handlePerPageChange(perPage.value);
  };

  const sort = (sortBy: string, sortOrder: string) => {
    handleSortChange(sortBy, sortOrder);
  };

  const tableClass = cn(
    dataTableStyles.dataTable,
    dataTableStyles.isHoverable,
    dataTableStyles.nowrapCells,
  );

  const emptyMessageClass = cn(
    dataTableStyles.emptyMessage,
    styles.emptyMessage,
    spacesStyles.ptxl,
    spacesStyles.pbxl,
  );

  const onSort = (value, order) => {
    if (value === 'stringTradeSize') {
      sort('tradeSize', order);
    }
    sort(value, order);
  };

  useEffect(() => {
    trackAnalytics({ module: TRACE_MODULE });
  }, []);

  return (
    <>
      <div className={ cn(dataTableStyles.dataTableContainer, styles.tableContainer) }>
        { isTraceFetching && (
          <Spinner overlay />
        ) }
        { data?.length === 0 && !isTraceFetching && (
          <div className={ emptyMessageClass }>
            { emptyMessage }
          </div>
        ) }

        { data?.length > 0 && (
          <table className={ tableClass } data-test="traceTable">
            <HeaderWrapper>
              <HeaderRow
                sortBy={ sortBy }
                sortOrder={ sortOrder }
                onSortChange={ onSort }
                columns={ getColumns() }
              />
            </HeaderWrapper>

            <BodyWrapper>
              { data?.length > 0 && data.map((row, index) => (
                <TradesRow
                  key={ row.id }
                  rowIndex={ index }
                  row={ row }
                />
              )) }
            </BodyWrapper>
          </table>
        ) }
      </div>
      <Paginator
        className={ traceStyles.tracePaginator }
        dataTest="traceTable"
        page={ page }
        perPage={ perPage }
        totalCount={ totalCount }
        onPageChange={ paginate }
        labelOfText={ <TableFooterLabel /> }
        onItemsPerPageChange={ itemsPerPage }
        isItemsPerPageVisible={ isItemsPerPageVisible }
      />
    </>
  );
};

export default TraceTable;
